<script setup lang="ts">
import {CircleCheckIcon} from "lucide-vue-next";
import type {HTMLAttributes, Ref} from "vue";
import {computed, onMounted, ref} from "vue";
import type {LocationResponse} from "../../../responses/LocationResponse.ts";
import type {ProductResponse} from "../../../responses/ProductResponse";
import {label} from "../../mixins/translations";
import {useProductStore} from "../../stores/product";

interface Props {
    product: ProductResponse,
    location?: string,
    class?: HTMLAttributes['class'],
    showImage?: boolean,
    layout?: 'grid' | 'list',
}

const props = withDefaults(defineProps<Props>(), {
    showImage: true,
    layout: 'grid',
});

const productStore = useProductStore();

const queryParameters: Ref<string> = ref('');

onMounted(() => {
    if (props.location) {
        queryParameters.value = `?straal=10&plaats=${encodeURIComponent(props.location)}`;
    }
})

const slug = computed(() => {
    return `${props.product.url}${queryParameters.value}`
})

</script>

<template>
    <article class="flex justify-between items-center w-full gap-2 md:hidden" v-if="layout === 'list'">
        <h2 class="!text-base !font-bold !leading-snug text-black !line-clamp-2">
            <a :href="slug">{{ product.name }}</a>
        </h2>
        <div>
            <a :href="slug"
               class="min-w-24 inline-flex flex-wrap items-center justify-center rounded-md border border-primary px-3 py-2 text-sm font-normal text-primary transition hover:bg-primary hover:text-white">
                {{ label('site.product.more_info') }}
            </a>
        </div>
    </article>
    <article class="grid h-full grid-rows-[auto_auto_1fr_auto] overflow-hidden rounded-md bg-white shadow-gray-100 shadow-md" :class="{'hidden md:grid' : layout === 'list'}">
        <div class="relative row-span-1">
            <div v-if="showImage">
                <a :href="slug" class="hidden lg:block">
                    <img
                        :src="product.thumbnail?.image_url"
                        :alt="product.thumbnail?.alt_text ?? product.name"
                        class="w-full h-60 max-h-60 object-cover"
                    />
                </a>

                <div
                    v-if="product.product_labels?.length"
                    class="relative lg:absolute top-0 left-0 lg:top-4 lg:left-4 p-4 pb-0 lg:p-0 flex gap-x-2"
                >
                    <span
                        v-for="label in product.product_labels"
                        :key="label.name"
                        class="rounded-full bg-secondary-50 px-3 py-1 text-sm font-medium text-secondary-800"
                    >
                      {{ label.name }}
                    </span>
                </div>
            </div>
        </div>

        <div class="grid grid-rows-subgrid row-span-3 p-6">
            <h2 class="mb-4 !min-h-[0] lg:!min-h-[3.5rem] !text-xl !font-extrabold !leading-snug text-black !line-clamp-2">
                <a :href="slug">{{ product.name }}</a>
            </h2>

            <ul class="mb-6 space-y-2 text-sm font-normal text-gray-600">
                <li
                    v-for="usp in product.usps"
                    :key="usp.id || usp.description"
                    class="flex items-start gap-2"
                >
                    <div class="flex shrink-0 -mt-[1px]">
                        <CircleCheckIcon :stroke-width="1.2" class="h-6 w-6 text-gray-400"/>
                    </div>
                    <div v-html="usp.description"></div>
                </li>
            </ul>

            <div class="flex justify-end gap-3 lg:hidden">
<!--                <a-->
<!--                    v-show="product.erp_id !== null"-->
<!--                    :href="`${slug}`"-->
<!--                    class="inline-flex items-center justify-center rounded-md bg-primary px-3 py-3 text-base font-semibold text-white transition hover:bg-primary-400"-->
<!--                >-->
<!--                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"-->
<!--                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"-->
<!--                         class="lucide lucide-arrow-up-right-icon lucide-arrow-up-right">-->
<!--                        <path d="M7 7h10v10"/>-->
<!--                        <path d="M7 17 17 7"/>-->
<!--                    </svg>-->
<!--                </a>-->
                <a
                    :href="slug"
                    class="inline-flex flex-1 items-center justify-center rounded-md border border-primary px-4 py-3 text-base font-normal text-primary transition hover:bg-primary hover:text-white"
                >
                    {{ label('site.product.more_info') }}
                </a>
            </div>

            <div class="gap-3 hidden lg:flex">
                <a
                    v-show="product.erp_id !== null"
                    :href="`${slug}#beschikbare-data`"
                    class="inline-flex flex-1 items-center justify-center rounded-md bg-primary px-4 py-3 text-base font-semibold text-white transition hover:bg-primary-400"
                >
                    {{ label('site.product.enroll') }}
                </a>
                <a
                    v-show="product.erp_id === null"
                    :href="label('site.product.request_offer_url')"
                    class="inline-flex flex-1 items-center justify-center rounded-md bg-primary px-4 py-3 text-md font-semibold text-white transition hover:bg-primary-400"
                >
                    {{ label('site.product.request_offer') }}
                </a>

                <a
                    :href="slug"
                    class="inline-flex flex-1 items-center justify-center rounded-md border border-primary px-4 py-3 text-base font-normal text-primary transition hover:bg-primary hover:text-white"
                >
                    {{ label('site.product.more_info') }}
                </a>
            </div>
        </div>
    </article>
</template>
